import React from 'react';
import { Container, Message, Text, Icon, Grid, List, Link } from 'basis';
import { LayoutPage, LayoutContent, SaveAndCompleteLaterButton } from 'components';
import styled from '@emotion/styled';
import { useStoreValue } from 'store';
import { useDataLayerAndAnalyticsLinks } from 'hooks';
import { STEP_VERIFY_INSTORE } from '_config';
import { device } from 'media';
import { AusPostAction } from '../aus-post/AusPostAction';
import { isCreditLine, isGO, isGemAU, isGemAUWithMerchantApple } from '../../utils';
import { VerifyIdentityInstoreOrAusPost } from './VerifyIdentityInstoreOrAusPost';

const IconWrapper = styled.span`
  position: relative;
  top: 5px;
  svg {
    width: 24px;
    height: 24px;
    display: inline;
    margin: 0 8px;
  }
`;

const ContentWrapper = styled.div`
  li::before {
    background-color: black;
    width: 0.3em;
    height: 0.3em;
  }

  hr {
    border: 1px solid #d6d6d6;
    margin: 40px 0;
  }

  img {
    height: auto;
    width: 120px;
  }

  div[data-testid='inner-container'] {
    box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.1);
  }

  @media ${device.mobileOrSmaller} {
    button {
      width: 100%;
    }
  }
`;

export const VerifyIdentityInstore = () => {
  const [storeState] = useStoreValue();
  if (isGemAUWithMerchantApple(storeState)) {
    return <VerifyIdentityInstoreOrAusPost />;
  }
  return <VerifyIdentityInstoreWithoutAusPost />;
};

export const VerifyIdentityInstoreWithoutAusPost = () => {
  const [store] = useStoreValue();
  const { trackEventSender } = useDataLayerAndAnalyticsLinks(store);
  const isGOOrGemAu = isGO() || isGemAU();
  const storeFinderLabel = isCreditLine() ? 'Find Apple Store' : 'Find a store';

  return (
    <LayoutPage hideBackButton>
      <LayoutContent rowsGap="0">
        <ContentWrapper>
          <Container padding="0 0 6 0" padding-md="0 0 8 0">
            <Message testId="pink-message" severity="info-or-minor" bg="secondary.pink.t30">
              <strong>Unfortunately, we weren't able to verify your identity electronically.</strong>
            </Message>
            <Text as="p" margin="6 0">
              Follow the steps below, and take the required documentation{isCreditLine() ? ' ' : ' in-store '}to{' '}
              {isCreditLine()
                ? 'your nearest Apple store'
                : isGO()
                ? 'your nearest Harvey Norman'
                : 'one of our merchant partners'}
              .
            </Text>
            <Text as="p" testId="application-ref">
              Application reference number: <strong>{store.applicationRef}</strong>
            </Text>
          </Container>
          <Container bg="grey.t03" padding="4" padding-md="10">
            <Container bg="white" padding="8" testId="inner-container">
              <Container>
                <AusPostAction order="1">
                  Take your identification documents {isGO() ? 'to a Harvey Norman' : 'in-store'}
                </AusPostAction>
                <Text as="p" margin="0 0 0 9">
                  Find your nearest{' '}
                  {isCreditLine() ? 'Apple Store' : isGO() ? 'Harvey Norman' : 'Latitude merchant partner'} and verify
                  your identity.
                </Text>
                <Grid margin="8 0 0 0">
                  <Link
                    newTab
                    appearance="primary-button"
                    testId="find-instore-form-btn"
                    href={store.applyAndBuy?.storeFinderUrl}
                    onClick={() => {
                      trackEventSender({
                        action: 'external-link',
                        location: STEP_VERIFY_INSTORE,
                        label: storeFinderLabel,
                      });
                    }}
                  >
                    <span>{storeFinderLabel}</span>
                    <IconWrapper>
                      <Icon name="external-link" color="white" />
                    </IconWrapper>
                  </Link>
                </Grid>
              </Container>
              <Container margin="8 0">
                <AusPostAction order="2">Identification documents must be originals and current</AusPostAction>
                <Text as="p" margin="0 0 8 9">
                  You <strong>MUST supply ONE primary photographic document</strong> from the list below.
                </Text>
              </Container>
              <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
                <List>
                  <List.Item>
                    If your <strong>primary photographic document</strong> contains your current residential address -
                    then no further identification is required.
                  </List.Item>
                  <List.Item>
                    <Container margin="6 0 0 0">
                      If your primary photographic document <strong>DOES NOT</strong> contain your current residential{' '}
                      address, then you must {isGOOrGemAu ? '' : 'also '}
                      <strong>supply ONE secondary document that contains your current residential address.</strong>.
                    </Container>
                  </List.Item>
                </List>
              </Message>
              <Container margin="6 0">
                <Text margin="3 0">
                  <strong>Accepted Primary Photographic ID Documents</strong>
                </Text>
                <List>
                  <List.Item>Australian Driver Licence (current)</List.Item>
                  <List.Item>Australian Learners Permit (current)</List.Item>
                  <List.Item>Australian Passport (not expired more than 2 years)</List.Item>
                  <List.Item>Foreign Passport (current only)</List.Item>
                  <List.Item>Proof of Age card/NSW Photo Card (current and government issued)</List.Item>
                </List>
              </Container>
              <Container margin="6 0">
                <Text margin="3 0">
                  <strong>Accepted Secondary ID Documents</strong>
                </Text>
                <List>
                  <List.Item>Utility Bill or Council Rates Notice (less than 3 months old)</List.Item>
                  <List.Item>Taxation notice or Centrelink statement (less than 12 months old)</List.Item>
                </List>
              </Container>
            </Container>
          </Container>
          <Container margin="8 0">
            <SaveAndCompleteLaterButton />
          </Container>
        </ContentWrapper>
      </LayoutContent>
    </LayoutPage>
  );
};
